import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Grid, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import { useCreateClient } from "../../hooks/clients/useCreateClient";
import { useUpdateClient } from "../../hooks/clients/useUpdateClient";
import FormInput from "../inputs/FormInput";
import LanguageToggle from "../global/LanguageToggle";

const FormAddEditClient = ({ currentData, isEdit = false, onSubmitSuccess }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");
    const initialLang = localStorage.getItem("i18nextLng") || i18next.language || "ar";
    const [activeLang, setActiveLang] = useState(initialLang.startsWith("ar") ? "ar" : "en");

    // Form States
    const [titleAr, setTitleAr] = useState("");
    const [titleEn, setTitleEn] = useState("");
    const [image, setImage] = useState(null);
    const [yearsOfExperience, setYearsOfExperience] = useState(0);
    const [happyClients, setHappyClients] = useState(0);
    const [projectsCompleted, setProjectsCompleted] = useState(0);
    const [showInHome, setShowInHome] = useState(1);

    // Hooks
    const { createClientMutate, isPending: isCreating } = useCreateClient();
    const { updateClientMutate, isPending: isUpdating } = useUpdateClient();

    const loadingSubmit = isCreating || isUpdating;

    // Fill form data in edit mode
    useEffect(() => {
        if (isEdit && currentData) {
            setTitleAr(currentData?.title?.ar || "");
            setTitleEn(currentData?.title?.en || "");
            setImage(currentData?.image || null);
            setYearsOfExperience(currentData?.years_of_experience ?? 0);
            setHappyClients(currentData?.happy_clients ?? 0);
            setProjectsCompleted(currentData?.projects_completed ?? 0);
            setShowInHome(currentData?.show_in_home ?? 1);
        }
    }, [isEdit, currentData]);

    const handleSubmit = (e) => {
        e.preventDefault();

        const payload = {
            title: { ar: titleAr || titleEn, en: titleEn || titleAr },
            years_of_experience: Number(yearsOfExperience),
            happy_clients: Number(happyClients),
            projects_completed: Number(projectsCompleted),
            show_in_home: Number(showInHome),
        };

        if (image) {
            payload.image = image;
        }

        if (isEdit) {
            updateClientMutate({ id: currentData.id, data: payload });
        } else {
            createClientMutate(payload);
        }
    };

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
            <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 2 }}>
                    <Typography variant="h6">
                        {isEdit ? (isArabic ? "تعديل العميل" : "Edit Client") : (isArabic ? "إضافة عميل جديد" : "Add New Client")}
                    </Typography>

                    <LanguageToggle activeLang={activeLang} onChange={(lng) => setActiveLang(lng)} showLabel={true} />
                </Box>

                <Grid container spacing={3}>
                    {/* Image at the top center */}
                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                        <FormInput
                            label={isArabic ? "الصورة" : "Image"}
                            type="file"
                            name="image"
                            value={image}
                            onChange={(e) => setImage(e.target.value)}
                            width={120}
                            height={120}
                        />
                    </Grid>

                    {/* Multilingual Titles */}
                    {activeLang === "ar" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label={isArabic ? "العنوان بالعربية" : "Arabic Title"}
                                value={titleAr}
                                onChange={(e) => setTitleAr(e.target.value)}
                                required
                                fullWidth
                            />
                        </Grid>
                    )}

                    {activeLang === "en" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label={isArabic ? "العنوان بالإنجليزية" : "English Title"}
                                value={titleEn}
                                onChange={(e) => setTitleEn(e.target.value)}
                                required
                                fullWidth
                            />
                        </Grid>
                    )}

                    {/* Years of Experience */}
                    <Grid item xs={12} md={4}>
                        <FormInput
                            type="number"
                            label={isArabic ? "سنوات الخبرة" : "Years of Experience"}
                            value={yearsOfExperience}
                            onChange={(e) => setYearsOfExperience(e.target.value)}
                            fullWidth
                        />
                    </Grid>

                    {/* Happy Clients */}
                    <Grid item xs={12} md={4}>
                        <FormInput
                            type="number"
                            label={isArabic ? "العملاء السعداء" : "Happy Clients"}
                            value={happyClients}
                            onChange={(e) => setHappyClients(e.target.value)}
                            fullWidth
                        />
                    </Grid>

                    {/* Projects Completed */}
                    <Grid item xs={12} md={4}>
                        <FormInput
                            type="number"
                            label={isArabic ? "المشاريع المنجزة" : "Projects Completed"}
                            value={projectsCompleted}
                            onChange={(e) => setProjectsCompleted(e.target.value)}
                            fullWidth
                        />
                    </Grid>

                    {/* Show In Home Switch */}
                    <Grid item xs={12} md={6}>
                        <FormInput
                            type="switch"
                            label={isArabic ? "عرض في الصفحة الرئيسية" : "Show in Home"}
                            name="show_in_home"
                            checked={!!showInHome}
                            onChange={(e) => setShowInHome(e.target.checked ? 1 : 0)}
                        />
                    </Grid>
                </Grid>

                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loadingSubmit}
                        sx={{ minWidth: 120 }}
                    >
                        {loadingSubmit ? (isArabic ? "جاري الحفظ..." : "Saving...") : (isArabic ? "حفظ" : "Save")}
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
};

export default FormAddEditClient;